import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { I18nProvider } from "./lib/i18n";
import { SettingsProvider } from "./lib/settings";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Appointments from "./pages/Appointments";
import MedicalHistory from "./pages/MedicalHistory";
import Prescriptions from "./pages/Prescriptions";
import Certificates from "./pages/Certificates";
import MedicalReports from "./pages/MedicalReports";
import Patients from "./pages/Patients";
import Interrogations from "./pages/Interrogations";
import AdminUsers from "./pages/AdminUsers";
import Availability from "./pages/Availability";
import Profile from "./pages/Profile";
import PaymentSuccess from "./pages/PaymentSuccess";
import Exams from "./pages/Exams";
import Messages from "./pages/Messages";
import PublicVerify from "./pages/PublicVerify";
import ActivateAccount from "./pages/ActivateAccount";
import PreInterrogation from "./pages/PreInterrogation";
import PublicChat from "./pages/PublicChat";
import Teleconsulta from "./pages/Teleconsulta";
import AdminSettings from "./pages/AdminSettings";
import AdminCompliance from "./pages/AdminCompliance";
import ClinicalHistory from "./pages/ClinicalHistory";
import MyHistory from "./pages/MyHistory";
import Dependents from "./pages/Dependents";
import LegalNotice from "./pages/LegalNotice";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import InformedConsent from "./pages/InformedConsent";
import CookiePolicy from "./pages/CookiePolicy";
import AIUsage from "./pages/AIUsage";
import CookieBanner from "./components/CookieBanner";
import Layout from "./components/Layout";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verify/:code" element={<PublicVerify />} />
      <Route path="/activar-cuenta/:token" element={<ActivateAccount />} />
      <Route path="/pre-interrogatorio/:token" element={<PreInterrogation />} />
      <Route path="/chat" element={<PublicChat />} />
      <Route path="/app" element={<Protected><Dashboard /></Protected>} />
      <Route path="/app/appointments" element={<Protected><Appointments /></Protected>} />
      <Route path="/app/availability" element={<Protected><Availability /></Protected>} />
      <Route path="/app/history" element={<Protected><MedicalHistory /></Protected>} />
      <Route path="/app/prescriptions" element={<Protected><Prescriptions /></Protected>} />
      <Route path="/app/certificados" element={<Protected><Certificates /></Protected>} />
      <Route path="/app/informes" element={<Protected><MedicalReports /></Protected>} />
      <Route path="/app/pacientes" element={<Protected roles={["doctor","admin"]}><Patients /></Protected>} />
      <Route path="/app/interrogatorios" element={<Protected><Interrogations /></Protected>} />
      <Route path="/app/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/app/exams" element={<Protected><Exams /></Protected>} />
      <Route path="/app/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/app/video/:roomId" element={<Protected><Teleconsulta /></Protected>} />
      <Route path="/app/consulta-presencial/:patientId" element={<Protected roles={["doctor", "admin"]}><Teleconsulta mode="in_person" /></Protected>} />
      <Route path="/app/consulta-telefonica/:patientId" element={<Protected roles={["doctor", "admin"]}><Teleconsulta mode="phone" /></Protected>} />
      <Route path="/app/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/app/users" element={<Protected roles={["admin"]}><AdminUsers /></Protected>} />
      <Route path="/app/settings" element={<Protected roles={["admin"]}><AdminSettings /></Protected>} />
      <Route path="/app/cumplimiento" element={<Protected roles={["admin"]}><AdminCompliance /></Protected>} />
      <Route path="/app/historia-clinica" element={<Protected roles={["doctor","admin"]}><ClinicalHistory /></Protected>} />
      <Route path="/app/mi-historia" element={<Protected roles={["patient"]}><MyHistory /></Protected>} />
      <Route path="/app/dependientes" element={<Protected roles={["patient"]}><Dependents /></Protected>} />
      <Route path="/aviso-legal" element={<LegalNotice />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />
      <Route path="/terminos" element={<Terms />} />
      <Route path="/consentimiento" element={<InformedConsent />} />
      <Route path="/politica-cookies" element={<CookiePolicy />} />
      <Route path="/uso-ia" element={<AIUsage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <SettingsProvider>
            <BrowserRouter>
              <AppRoutes />
              <CookieBanner />
              <Toaster position="bottom-right" richColors />
            </BrowserRouter>
          </SettingsProvider>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
